import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CONCEPT_CONNECTIONS, CONCEPT_SHORT_NAMES } from '../data/prerequisites';
import { units } from '../data/units';

/* ─── Constants ────────────────────────────────────────────────────────────── */

const CANVAS_W = 900;
const CANVAS_H = 600;
const NODE_RADIUS = 24;
const NODE_FONT = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const NODE_FONT_BOLD = 'bold 10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/* ─── Unit colors ──────────────────────────────────────────────────────────── */

const UNIT_COLORS: Record<string, string> = {
  kinematics: '#2196F3',
  dynamics: '#F44336',
  'circular-motion': '#9C27B0',
  energy: '#FF9800',
  momentum: '#4CAF50',
  shm: '#00BCD4',
  torque: '#795548',
};

const UNIT_LABELS: Record<string, string> = {
  kinematics: 'Kinematics',
  dynamics: 'Dynamics',
  'circular-motion': 'Circular Motion',
  energy: 'Energy',
  momentum: 'Momentum',
  shm: 'SHM',
  torque: 'Torque',
};

/* ─── Concept-to-unit mapping ──────────────────────────────────────────────── */

const CONCEPT_UNIT: Record<string, string> = {};
units.forEach((unit) => {
  unit.concepts.forEach((concept) => {
    CONCEPT_UNIT[concept.id] = unit.id;
  });
});

/* ─── Static layout ────────────────────────────────────────────────────────── */

const LAYERS: string[][] = [
  /* 0 */ ['position-displacement'],
  /* 1 */ ['velocity-speed'],
  /* 2 */ ['acceleration', 'newtons-first-law'],
  /* 3 */ ['kinematic-equations', 'newtons-second-law', 'motion-graphs'],
  /* 4 */ [
    'free-fall', 'projectile-motion', 'newtons-third-law', 'free-body-diagrams',
    'uniform-circular-motion', 'work', 'linear-momentum', 'oscillations', 'angular-quantities',
  ],
  /* 5 */ [
    'friction', 'tension-normal', 'centripetal-force', 'gravitational-force',
    'kinetic-energy', 'potential-energy', 'power', 'impulse',
    'conservation-of-momentum', 'springs', 'pendulums', 'torque',
  ],
  /* 6 */ [
    'inclined-planes', 'orbits', 'conservation-of-energy', 'work-energy-theorem',
    'collisions', 'energy-in-shm', 'rotational-inertia',
  ],
  /* 7 */ ['rotational-dynamics', 'angular-momentum'],
];

interface NodePos {
  x: number;
  y: number;
  id: string;
}

function computeNodePositions(): NodePos[] {
  const positions: NodePos[] = [];
  const totalLayers = LAYERS.length;
  const marginX = 60;
  const marginY = 50;
  const usableW = CANVAS_W - marginX * 2;
  const usableH = CANVAS_H - marginY * 2;
  const layerSpacing = usableW / (totalLayers - 1);

  LAYERS.forEach((layer, layerIndex) => {
    const x = marginX + layerIndex * layerSpacing;
    const count = layer.length;
    const nodeSpacing = usableH / (count + 1);

    layer.forEach((conceptId, nodeIndex) => {
      const y = marginY + (nodeIndex + 1) * nodeSpacing;
      positions.push({ x, y, id: conceptId });
    });
  });

  return positions;
}

const NODE_POSITIONS = computeNodePositions();
const NODE_MAP: Record<string, NodePos> = {};
NODE_POSITIONS.forEach((n) => { NODE_MAP[n.id] = n; });

/* ─── Edge list from CONCEPT_CONNECTIONS ───────────────────────────────────── */

interface Edge {
  from: string;
  to: string;
}

const EDGES: Edge[] = [];
Object.entries(CONCEPT_CONNECTIONS).forEach(([from, toList]) => {
  toList.forEach((to) => {
    if (NODE_MAP[from] && NODE_MAP[to]) {
      EDGES.push({ from, to });
    }
  });
});

/* ─── Arrow drawing helper ─────────────────────────────────────────────────── */

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number,
  x2: number, y2: number,
  color: string,
  alpha: number,
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return;

  const ux = dx / dist;
  const uy = dy / dist;

  // Shorten line by node radius on each end
  const sx = x1 + ux * NODE_RADIUS;
  const sy = y1 + uy * NODE_RADIUS;
  const ex = x2 - ux * NODE_RADIUS;
  const ey = y2 - uy * NODE_RADIUS;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(ex, ey);
  ctx.stroke();

  // Arrowhead
  const headLen = 8;
  const angle = Math.atan2(ey - sy, ex - sx);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(ex, ey);
  ctx.lineTo(
    ex - headLen * Math.cos(angle - Math.PI / 6),
    ey - headLen * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    ex - headLen * Math.cos(angle + Math.PI / 6),
    ey - headLen * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/* ─── Component ────────────────────────────────────────────────────────────── */

const ConceptMapScreen: React.FC = () => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  // Compute which nodes are connected to the hovered node
  const getConnected = useCallback((nodeId: string | null): Set<string> => {
    if (!nodeId) return new Set();
    const connected = new Set<string>();
    connected.add(nodeId);
    EDGES.forEach((edge) => {
      if (edge.from === nodeId) connected.add(edge.to);
      if (edge.to === nodeId) connected.add(edge.from);
    });
    return connected;
  }, []);

  // Draw the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const connected = getConnected(hoveredNode);
    const hasHover = hoveredNode !== null;

    // Draw edges
    EDGES.forEach((edge) => {
      const fromNode = NODE_MAP[edge.from];
      const toNode = NODE_MAP[edge.to];
      if (!fromNode || !toNode) return;

      const isHighlighted = hasHover && connected.has(edge.from) && connected.has(edge.to);
      const alpha = hasHover ? (isHighlighted ? 0.9 : 0.08) : 0.25;
      const color = isHighlighted
        ? UNIT_COLORS[CONCEPT_UNIT[edge.from]] || '#666'
        : '#b0bec5';

      drawArrow(ctx, fromNode.x, fromNode.y, toNode.x, toNode.y, color, alpha);
    });

    // Draw nodes
    NODE_POSITIONS.forEach((node) => {
      const unitId = CONCEPT_UNIT[node.id];
      const color = UNIT_COLORS[unitId] || '#666';
      const label = CONCEPT_SHORT_NAMES[node.id] || node.id;

      const isHovered = node.id === hoveredNode;
      const isConnected = connected.has(node.id);
      const dimmed = hasHover && !isConnected;

      const alpha = dimmed ? 0.15 : 1;

      ctx.save();
      ctx.globalAlpha = alpha;

      // Node shadow
      if (isHovered) {
        ctx.shadowColor = color;
        ctx.shadowBlur = 12;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2;
      }

      // Node circle
      ctx.beginPath();
      ctx.arc(node.x, node.y, isHovered ? NODE_RADIUS + 3 : NODE_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      // Border
      ctx.strokeStyle = isHovered ? '#ffffff' : 'rgba(255,255,255,0.4)';
      ctx.lineWidth = isHovered ? 3 : 1.5;
      ctx.stroke();

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Label text
      ctx.font = isHovered ? NODE_FONT_BOLD : NODE_FONT;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Word wrap for long labels
      const words = label.split(' ');
      if (words.length > 1 && ctx.measureText(label).width > NODE_RADIUS * 1.6) {
        const mid = Math.ceil(words.length / 2);
        const line1 = words.slice(0, mid).join(' ');
        const line2 = words.slice(mid).join(' ');
        ctx.fillText(line1, node.x, node.y - 5, NODE_RADIUS * 2 - 4);
        ctx.fillText(line2, node.x, node.y + 7, NODE_RADIUS * 2 - 4);
      } else {
        ctx.fillText(label, node.x, node.y, NODE_RADIUS * 2 - 4);
      }

      ctx.restore();
    });

    // Tooltip for hovered node
    if (hoveredNode && NODE_MAP[hoveredNode]) {
      const node = NODE_MAP[hoveredNode];
      const unitId = CONCEPT_UNIT[hoveredNode];
      const unitData = units.find((u) => u.id === unitId);
      const conceptData = unitData?.concepts.find((c) => c.id === hoveredNode);
      if (conceptData) {
        const tooltipText = conceptData.title;
        ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        const textWidth = ctx.measureText(tooltipText).width;
        const padding = 10;
        const tooltipW = textWidth + padding * 2;
        const tooltipH = 28;

        let tx = node.x - tooltipW / 2;
        let ty = node.y - NODE_RADIUS - tooltipH - 8;

        // Keep tooltip inside canvas
        if (tx < 4) tx = 4;
        if (tx + tooltipW > CANVAS_W - 4) tx = CANVAS_W - 4 - tooltipW;
        if (ty < 4) ty = node.y + NODE_RADIUS + 8;

        ctx.fillStyle = 'rgba(33, 33, 33, 0.92)';
        ctx.beginPath();
        const r = 6;
        ctx.moveTo(tx + r, ty);
        ctx.lineTo(tx + tooltipW - r, ty);
        ctx.quadraticCurveTo(tx + tooltipW, ty, tx + tooltipW, ty + r);
        ctx.lineTo(tx + tooltipW, ty + tooltipH - r);
        ctx.quadraticCurveTo(tx + tooltipW, ty + tooltipH, tx + tooltipW - r, ty + tooltipH);
        ctx.lineTo(tx + r, ty + tooltipH);
        ctx.quadraticCurveTo(tx, ty + tooltipH, tx, ty + tooltipH - r);
        ctx.lineTo(tx, ty + r);
        ctx.quadraticCurveTo(tx, ty, tx + r, ty);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(tooltipText, tx + tooltipW / 2, ty + tooltipH / 2);
      }
    }
  }, [hoveredNode, getConnected]);

  // Mouse event handling
  const getMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>): { mx: number; my: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { mx: 0, my: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    return {
      mx: (e.clientX - rect.left) * scaleX,
      my: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const findNodeAt = useCallback((mx: number, my: number): string | null => {
    for (const node of NODE_POSITIONS) {
      const dx = mx - node.x;
      const dy = my - node.y;
      if (dx * dx + dy * dy <= (NODE_RADIUS + 4) * (NODE_RADIUS + 4)) {
        return node.id;
      }
    }
    return null;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const { mx, my } = getMousePos(e);
    const nodeId = findNodeAt(mx, my);
    setHoveredNode(nodeId);

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = nodeId ? 'pointer' : 'default';
    }
  }, [getMousePos, findNodeAt]);

  const handleMouseLeave = useCallback(() => {
    setHoveredNode(null);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const { mx, my } = getMousePos(e);
    const nodeId = findNodeAt(mx, my);
    if (nodeId) {
      const unitId = CONCEPT_UNIT[nodeId];
      if (unitId) {
        navigate(`/unit/${unitId}/concept/${nodeId}/lesson`);
      }
    }
  }, [getMousePos, findNodeAt, navigate]);

  return (
    <Container>
      <Header $color="#37474f">
        <HeaderInner>
          <BackButton onClick={() => navigate('/')}>
            <BackArrow>&larr;</BackArrow>
            <span>Back to Home</span>
          </BackButton>
          <HeaderTitle>
            <HeaderIcon>&#x1F5FA;&#xFE0F;</HeaderIcon>
            <HeaderText>
              <ConceptTitle>Concept Map</ConceptTitle>
              <ConceptSub>See how physics concepts connect</ConceptSub>
            </HeaderText>
          </HeaderTitle>
        </HeaderInner>
      </Header>

      <Main>
        <CanvasCard>
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            style={{
              width: '100%',
              maxWidth: CANVAS_W,
              height: 'auto',
              display: 'block',
              borderRadius: '12px',
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          />
        </CanvasCard>

        <Legend>
          <LegendTitle>Units</LegendTitle>
          <LegendGrid>
            {Object.entries(UNIT_COLORS).map(([unitId, color]) => (
              <LegendItem key={unitId}>
                <LegendDot $color={color} />
                <LegendLabel>{UNIT_LABELS[unitId]}</LegendLabel>
              </LegendItem>
            ))}
          </LegendGrid>
        </Legend>

        <HelpText>
          Hover over a node to highlight connections. Click a node to open its lesson.
        </HelpText>
      </Main>
    </Container>
  );
};

export default ConceptMapScreen;

/* ─── Styled Components ────────────────────────────────────────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.header<{ $color: string }>`
  background: linear-gradient(
    135deg,
    ${(props) => props.$color} 0%,
    ${(props) => props.$color}dd 100%
  );
  padding: 24px 24px 32px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 350px;
    height: 350px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }
`;

const HeaderInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 1;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  padding: 8px 14px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  align-self: flex-start;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BackArrow = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const HeaderIcon = styled.span`
  font-size: 32px;
  line-height: 1;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ConceptTitle = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.3px;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

const ConceptSub = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: 1.4;
`;

const Main = styled.main`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px 60px;
  position: relative;
  z-index: 2;
`;

const CanvasCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  margin-top: -16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const Legend = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
`;

const LegendTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px 0;
`;

const LegendGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${(props) => props.$color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #5f6577;
`;

const HelpText = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: #8890a4;
  text-align: center;
  margin: 16px 0 0 0;
  line-height: 1.5;
`;
